// Assignments flyer — a printable modal that shows every upcoming
// match with its two assigned umpires. Meant for the committee to
// share on WhatsApp / email once picks are locked in.
//
// Print → window.print() (a @media print rule in styles.css hides
// everything except the flyer body when printing).
// Share → captures the flyer as PNG and routes through the native
// share sheet, clipboard, or download depending on capability.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// See the comment in ./assignmentsShare.js — html2canvas-pro adds
// support for color-mix() and other modern CSS color functions.
import html2canvas from "html2canvas-pro";
import {
  X, Printer, MapPin, Clock, Gavel, Trophy, CalendarClock, Phone as PhoneIcon,
  Share2
} from "lucide-react";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import { fmtMatchDate, fmt12 } from "../shared";

// Snapshot the flyer and route it through the best share channel:
//   1. Native Web Share with files  (mobile — WhatsApp / Messages / Mail)
//   2. Clipboard image              (desktop — paste into WhatsApp Web etc.)
//   3. PNG download                 (final fallback)
async function shareFlyer(node, summary) {
  const canvas = await html2canvas(node, {
    backgroundColor: "#ffffff",
    scale: 3,
    useCORS: true,
    logging: false
  });
  const blob = await new Promise(r => canvas.toBlob(r, "image/png"));
  const file = new File([blob], "umpiring-assignments.png", { type: "image/png" });

  if (typeof navigator !== "undefined"
      && typeof navigator.canShare === "function"
      && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        text: summary,
        title: "Umpiring Assignments"
      });
      return { shared: true };
    } catch (err) {
      if (err?.name === "AbortError") return { cancelled: true };
    }
  }

  try {
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      return { clipboard: true };
    }
  } catch { /* fall through */ }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "umpiring-assignments.png";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { downloaded: true };
}

function buildSummary(rows) {
  if (!rows.length) return "Umpiring Assignments";
  const dates = [...new Set(rows.map(r => r.date))].sort();
  const range = dates.length === 1
    ? fmtMatchDate(dates[0])
    : `${fmtMatchDate(dates[0])} – ${fmtMatchDate(dates[dates.length - 1])}`;
  return `Umpiring Assignments — ${range}. ${rows.length} match${rows.length === 1 ? "" : "es"}.`;
}

export default function AssignmentsFlyer({ onClose }) {
  const toast = useToast();
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [sharing,  setSharing]  = useState(false);
  const flyerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.listAssignmentFlyer();
        setRows(list);
      } catch (err) {
        toast.error(err.message || "Couldn't load flyer data.");
      } finally { setLoading(false); }
    })();
  }, [toast]);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const withAssigned = rows.filter(m => (m.umpires || []).length > 0);

  return createPortal((
    <div className="modal" onClick={onClose}>
      <div
        className="modalInner ump-flyerModal"
        onClick={e => e.stopPropagation()}
      >
        <button type="button" className="close" onClick={onClose} aria-label="Close">
          <X size={20}/>
        </button>

        <div className="ump-flyer" role="document" ref={flyerRef}>
          {/* Header */}
          <header className="ump-flyer-head">
            <div>
              <span className="ump-flyer-eyebrow">
                <CalendarClock size={11}/> Umpiring Assignments
              </span>
              <h2>Pittsburgh Cricket League</h2>
              <p>Upcoming Match Officials</p>
            </div>
            <div className="ump-flyer-crest">
              <Gavel size={22}/>
            </div>
          </header>

          {loading && <p style={{color:"var(--muted)", padding:"20px 24px"}}>Loading assignments…</p>}
          {!loading && withAssigned.length === 0 && (
            <p style={{color:"var(--muted)", padding:"20px 24px"}}>
              No matches have assigned umpires yet.
            </p>
          )}

          <div className="ump-flyer-grid">
            {withAssigned.map(m => (
              <article key={m.id} className="ump-flyerCard">
                <header className="ump-flyerCard-head">
                  <span className="ump-flyerCard-series">
                    <Trophy size={10}/> {m.series}
                  </span>
                  <span className="ump-flyerCard-date">
                    {fmtMatchDate(m.date)}
                    {m.time && <> · {fmt12(m.time)}</>}
                  </span>
                </header>

                <div className="ump-flyerCard-teams">
                  <b>{m.teamOne}</b>
                  <span>vs</span>
                  <b>{m.teamTwo}</b>
                </div>

                <div className="ump-flyerCard-ground">
                  <MapPin size={11}/> {m.ground}
                </div>

                <div className="ump-flyerCard-umps">
                  {m.umpires.map(u => (
                    <div key={u.id} className="ump-flyerCard-ump">
                      <div className="ump-flyerCard-umpAvatar">
                        <Gavel size={11}/>
                      </div>
                      <div>
                        <b>{u.firstName} {u.lastName || ""}</b>
                        <small>
                          {u.teamName && <>{u.teamName}</>}
                          {u.teamName && u.phone && " · "}
                          {u.phone && <><PhoneIcon size={9}/> {u.phone}</>}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <footer className="ump-flyer-foot">
            <span>Prepared by the Committee · {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          </footer>
        </div>

        <div className="ump-flyer-actions">
          <button type="button" className="dark" onClick={onClose}>Close</button>
          <button
            type="button"
            className="btn"
            disabled={sharing || loading || withAssigned.length === 0}
            onClick={async () => {
              if (!flyerRef.current) return;
              setSharing(true);
              try {
                const r = await shareFlyer(flyerRef.current, buildSummary(withAssigned));
                if (r.clipboard) {
                  toast.info("Flyer copied to clipboard. Paste it anywhere (Ctrl+V / ⌘V) — WhatsApp, Messages, email, etc.", { duration: 7000 });
                } else if (r.downloaded) {
                  toast.info("Flyer downloaded. Attach it to whatever you're sharing.", { duration: 7000 });
                }
              } catch (err) {
                toast.error("Share failed: " + (err.message || err));
              } finally {
                setSharing(false);
              }
            }}
            title={withAssigned.length === 0
              ? "Nothing to share yet — assign umpires first"
              : "Share via WhatsApp, Messages, email…"}
          >
            <Share2 size={14}/> {sharing ? "Preparing…" : "Share"}
          </button>
          <button type="button" className="btn" onClick={() => window.print()}>
            <Printer size={14}/> Print / Save PDF
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
